import { useEffect, useRef, useState } from "react";
import { detectPrint, sampleForDetection } from "../../lib/scan/detect";
import { smoothQuad, type Quad } from "../../lib/scan/geometry";
import { fileToCanvas, toCanvas } from "../../lib/scan/image";

interface CameraCaptureProps {
  onCapture: (canvas: HTMLCanvasElement) => void;
  onClose: () => void;
}

// Not in every TypeScript DOM lib yet; Chrome on Android has it, Safari doesn't.
declare global {
  interface Window {
    ImageCapture?: new (track: MediaStreamTrack) => { takePhoto(): Promise<Blob> };
  }
}

/**
 * Full-screen rear-camera viewfinder with a live outline around the print
 * it can see. Falls back to the system camera / photo library through a
 * file input when the camera API is missing or permission is refused.
 */
export function CameraCapture({ onCapture, onClose }: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const sampleCanvas = useRef<HTMLCanvasElement | null>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const libraryInput = useRef<HTMLInputElement>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [quad, setQuad] = useState<Quad | null>(null);
  const [videoSize, setVideoSize] = useState({ w: 4, h: 3 });
  const [busy, setBusy] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("This browser can't use the camera from the app.");
      return;
    }
    navigator.mediaDevices
      .getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 3840 },
          height: { ideal: 2160 },
        },
      })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const v = videoRef.current;
        if (v) {
          v.srcObject = stream;
          void v.play().catch(() => {});
        }
      })
      .catch((err: unknown) => {
        const name = err instanceof DOMException ? err.name : "";
        setCameraError(
          name === "NotAllowedError"
            ? "Camera access was turned off for this site."
            : "The camera couldn't be started.",
        );
      });
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  // Live outline, a few times a second.
  useEffect(() => {
    if (!ready) return;
    sampleCanvas.current ??= document.createElement("canvas");
    let prev: Quad | null = null;
    let misses = 0;
    const id = window.setInterval(() => {
      const v = videoRef.current;
      if (!v || v.readyState < 2 || !v.videoWidth) return;
      const img = sampleForDetection(v, v.videoWidth, v.videoHeight, sampleCanvas.current!);
      const found = img ? detectPrint(img) : null;
      if (found) {
        misses = 0;
        prev = smoothQuad(prev, found);
        setQuad(prev);
      } else if (++misses > 3) {
        // Keep the last outline through a dropped frame or two.
        prev = null;
        setQuad(null);
      }
    }, 220);
    return () => window.clearInterval(id);
  }, [ready]);

  async function shoot() {
    const v = videoRef.current;
    const track = streamRef.current?.getVideoTracks()[0];
    if (!v || !v.videoWidth || busy) return;
    setBusy(true);
    try {
      // A still from ImageCapture is full sensor resolution; a video frame
      // is whatever the stream negotiated (often 1080p on iPhones).
      if (track && window.ImageCapture) {
        try {
          // Some cameras take seconds to answer, or never do; don't leave
          // the shutter looking dead.
          const blob = await Promise.race([
            new window.ImageCapture(track).takePhoto(),
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error("takePhoto timed out")), 2500),
            ),
          ]);
          onCapture(await fileToCanvas(blob));
          return;
        } catch {
          // Fall through to the video frame.
        }
      }
      onCapture(toCanvas(v, v.videoWidth, v.videoHeight));
    } finally {
      setBusy(false);
    }
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setFileError(null);
    setBusy(true);
    try {
      onCapture(await fileToCanvas(file));
    } catch (err) {
      setFileError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const { w, h } = videoSize;
  const points = quad
    ? [quad.tl, quad.tr, quad.br, quad.bl].map((p) => `${p.x * w},${p.y * h}`).join(" ")
    : null;

  return (
    <div className="scan-camera">
      <input ref={cameraInput} type="file" accept="image/*" capture="environment" hidden onChange={(e) => void onFile(e)} />
      <input ref={libraryInput} type="file" accept="image/*" hidden onChange={(e) => void onFile(e)} />

      <button type="button" className="scan-camera__close" onClick={onClose} aria-label="Close scanner">
        ✕
      </button>

      {cameraError ? (
        <div className="scan-camera__fallback">
          <p>{cameraError}</p>
          <p className="scan-camera__hint">You can still take the picture with your phone's camera.</p>
          <button type="button" onClick={() => cameraInput.current?.click()} disabled={busy}>
            Take a photo
          </button>
          <button type="button" className="btn-secondary" onClick={() => libraryInput.current?.click()} disabled={busy}>
            Choose from your photos
          </button>
          {fileError && <p className="text-error">{fileError}</p>}
        </div>
      ) : (
        <>
          <div className="scan-camera__view">
            <video
              ref={videoRef}
              playsInline
              muted
              onLoadedMetadata={(e) => {
                const v = e.currentTarget;
                setVideoSize({ w: v.videoWidth || 4, h: v.videoHeight || 3 });
                setReady(true);
              }}
            />
            <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
              {points && (
                <polygon
                  points={points}
                  fill="rgba(80, 200, 120, 0.18)"
                  stroke="#5ad17f"
                  strokeWidth={Math.max(w, h) / 200}
                  strokeLinejoin="round"
                />
              )}
            </svg>
          </div>
          <p className="scan-camera__hint">
            {quad
              ? "Photo found. Hold steady and tap the button."
              : "Lay the photo flat on a dark, plain surface. Avoid lamp glare."}
          </p>
          <div className="scan-camera__controls">
            <button
              type="button"
              className="scan-camera__side-btn"
              onClick={() => libraryInput.current?.click()}
              disabled={busy}
            >
              Photos
            </button>
            <button
              type="button"
              className="scan-camera__shutter"
              onClick={() => void shoot()}
              disabled={!ready || busy}
              aria-label="Take picture"
            />
            {/* The live view is limited to video resolution (often 1080p on
                iPhones, which have no ImageCapture); the system camera
                always takes a full-resolution still. */}
            <button
              type="button"
              className="scan-camera__side-btn"
              onClick={() => cameraInput.current?.click()}
              disabled={busy}
            >
              Camera app
            </button>
          </div>
          {fileError && <p className="text-error" style={{ textAlign: "center" }}>{fileError}</p>}
        </>
      )}
    </div>
  );
}
